import { useEffect, useState } from 'react';
import { api, type MagentoStorefront, type StorefrontBuild, type StorefrontMode } from '../api';
import { useResource } from '../hooks';
import { Callout, Field, Section, Select } from './primitives';
import { FactList, QuietEmpty } from './project/ui';
import { relativeTime } from './project/projectHealth';
import { ErrorNotice, LoadingRows } from './ui';

const modeLabel: Record<StorefrontMode, string> = {
  auto: 'Automatic',
  luma: 'Luma (Magento’s own themes)',
  hyva: 'Hyvä',
  headless: 'Headless (separate storefront applications)',
};

function sourceText(sf: MagentoStorefront): string {
  switch (sf.effective.source) {
    case 'configured':
      return 'set on this project';
    case 'project':
      return 'every website is served by a headless storefront';
    case 'default':
      return 'nothing detected yet: Magento’s own frontend';
  }
  if (sf.effective.resolve_at_build) {
    return 'Hyvä is installed; the theme is read from the release when it is built';
  }
  const commit = sf.detected_commit ? sf.detected_commit.slice(0, 7) : '';
  const where = sf.detected_from === 'release' ? 'built release' : sf.detected_from === 'deployment' ? 'deployed commit' : 'repository';
  return `detected from the ${where}${commit ? ` at ${commit}` : ''}${sf.detected_at ? `, ${relativeTime(sf.detected_at)}` : ''}`;
}

function buildText(b: StorefrontBuild): string {
  const install = b.package_manager === 'npm' ? 'npm ci' : b.package_manager === 'pnpm' ? 'pnpm install --frozen-lockfile' : 'yarn install (frozen lockfile)';
  const node = b.node_major ? `Node ${b.node_major}` : 'Node';
  return `${install} from ${b.lockfile || 'no lockfile'}, then ${b.package_manager} run ${b.script || 'build'} on ${node}`;
}

/**
 * StorefrontCard is what the Magento store's storefront is built with —
 * Luma, Hyvä or a headless frontend — as Commerce Ops read it from the
 * repository, and therefore what deployments build and check. A Hyvä
 * store's Tailwind builds are listed with how they are installed and run.
 * The choice is automatic; the override is for an ambiguous or wrong
 * detection only.
 */
export function StorefrontCard({ projectId, canEdit = true }: { projectId: string; canEdit?: boolean }) {
  const [reload, setReload] = useState(0);
  const view = useResource(() => api.getMagentoStorefront(projectId), [projectId, reload], projectId, { key: `project:${projectId}:storefront` });
  const sf = view.data?.storefront;
  const [mode, setMode] = useState<StorefrontMode>('auto');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (sf) setMode(sf.mode || 'auto');
  }, [sf?.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = 'Storefront';

  if (view.loading && !sf) {
    return (
      <Section title={title} testId="storefront-card">
        <LoadingRows label="Reading the storefront" rows={4} rowHeight="22px" />
      </Section>
    );
  }
  if (!sf) {
    return (
      <Section title={title} testId="storefront-card">
        <ErrorNotice error={view.error} />
      </Section>
    );
  }

  async function save() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api.setMagentoStorefrontMode(projectId, mode);
      setSaved(true);
      setReload((v) => v + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSaving(false);
    }
  }

  const d = sf.detection;
  const eff = sf.effective;
  const builds = (d?.themes ?? []).filter((t) => t.build).map((t) => t.build as StorefrontBuild);
  const ownHyva = (d?.themes ?? []).filter((t) => t.hyva && (t.location === 'app' || t.location === 'package'));
  const unresolved = d?.unresolved_theme_packages ?? [];
  const themeModule = d?.hyva_packages?.find((p) => p.name === 'hyva-themes/magento2-theme-module') ?? d?.hyva_packages?.[0];
  const detectedLabel = d ? (d.storefront === 'hyva' ? 'Hyvä' : d.storefront === 'luma' ? 'Luma' : 'not determined') : 'not read yet';
  const changed = mode !== (sf.mode || 'auto');

  return (
    <Section title={title} testId="storefront-card">
      <ErrorNotice error={error ?? view.error} />
      {eff.problems && eff.problems.length > 0 ? (
        <Callout tone="danger" title="The Hyvä frontend cannot be built from the last commit read" testId="storefront-problems">
          <ul className="storefront-card__list">
            {eff.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <p>Deployments of such a commit are refused before anything is built. Fix the repository and deploy again.</p>
        </Callout>
      ) : null}
      {eff.ambiguities && eff.ambiguities.length > 0 ? (
        <Callout tone="warning" title="The repository does not settle the storefront" testId="storefront-ambiguities">
          <ul className="storefront-card__list">
            {eff.ambiguities.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      <div className="varnish-card__grid">
        <FactList
          facts={[
            {
              label: 'Storefront',
              value: (
                <span className="storefront-card__type">
                  <span className="badge" data-testid="storefront-type">
                    {sf.label}
                  </span>
                  <span className="muted">{sourceText(sf)}</span>
                </span>
              ),
            },
            ...(themeModule ? [{ label: 'Hyvä', value: <span data-testid="storefront-hyva-version">{`${themeModule.version} (${d?.hyva_packages?.length ?? 0} packages)`}</span> }] : []),
            ...(ownHyva.length > 0
              ? [
                  {
                    label: ownHyva.length === 1 ? 'Theme' : 'Themes',
                    value: (
                      <span data-testid="storefront-themes">
                        {ownHyva.map((t) => `${t.code}${t.parent ? ` (child of ${t.parent})` : ''}${t.package ? `, from package ${t.package}` : ''}`).join('; ')}
                      </span>
                    ),
                  },
                ]
              : []),
            ...(unresolved.length > 0
              ? [
                  {
                    label: unresolved.length === 1 ? 'Theme package' : 'Theme packages',
                    value: (
                      <span data-testid="storefront-theme-packages">
                        {unresolved.join(', ')}
                        <span className="muted"> — installed by Composer on the server, not in the repository</span>
                      </span>
                    ),
                  },
                ]
              : []),
            ...(d?.active_themes && d.active_themes.length > 0 ? [{ label: 'Assigned in config.php', value: d.active_themes.join(', ') }] : []),
          ]}
        />
        {builds.length > 0 ? (
          <FactList
            facts={builds.flatMap((b) => [
              { label: builds.length > 1 ? `${b.theme} build` : 'Frontend build', value: <span data-testid="storefront-build">{buildText(b)}</span> },
              { label: builds.length > 1 ? `${b.theme} output` : 'Built stylesheet', value: <code className="storefront-card__path">{b.output}</code> },
            ])}
          />
        ) : eff.resolve_at_build ? (
          <QuietEmpty>
            Build not read yet · the next deployment records it
          </QuietEmpty>
        ) : eff.type === 'hyva' ? (
          <QuietEmpty>No own Tailwind build; the themes’ shipped stylesheets are used</QuietEmpty>
        ) : null}
      </div>
      {d?.warnings && d.warnings.length > 0 ? (
        <details className="section-help">
          <summary>{d.warnings.length === 1 ? '1 note from the detection' : `${d.warnings.length} notes from the detection`}</summary>
          <ul className="section-help__body storefront-card__list">
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {canEdit ? (
        <div className="storefront-card__mode-block">
          <div className="storefront-card__mode">
            <Field label="How the storefront is decided">
              <Select name="storefront-mode" autoComplete="off" aria-describedby="storefront-mode-hint" value={mode} onChange={(e) => { setMode(e.target.value as StorefrontMode); setSaved(false); }} data-testid="storefront-mode">
                {(Object.keys(modeLabel) as StorefrontMode[]).map((m) => (
                  <option key={m} value={m}>
                    {m === 'auto' ? `Automatic (detected: ${detectedLabel})` : modeLabel[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <button type="button" className="button button--secondary button--small" onClick={save} disabled={!changed || saving} data-testid="storefront-mode-save">
              {saving ? 'Saving' : 'Save'}
            </button>
            {saved && !changed ? (
              <span className="muted" role="status">
                Saved
              </span>
            ) : null}
          </div>
          <p id="storefront-mode-hint" className="field__hint">
            Choose a type only when the detection is ambiguous or wrong.
          </p>
        </div>
      ) : null}
    </Section>
  );
}
